import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Breadcrumbs } from "../../AbstractElements";
import { Button, Card, CardBody, Col, Row } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useLoader } from "../../_helper/LoaderContext";
import { toast } from "react-toastify";
import { ArrowLeft } from "react-feather";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import { getItemDetails, updateItemInfo } from "../PharmacyMedicineAdd/PharmacyMedicineService";
import {
	appendOptionalItemLists,
	getInteractionsList,
	getPrimaryUseList,
} from "../PharmacyMedicineAdd/utils/itemListFields";
import { getItemMetaValue, getPackagingDetail } from "../PharmacyMedicineAdd/utils/productMetaFields";
import { useDisableScrollOnFocus } from "../../Hooks/useDisableScrollOnFocus";
import { generateInternalEan13 } from "../../utils/internalEan13";
import BarcodePrintModal from "../Common/Barcode/BarcodePrintModal";
import {
	sanitizeBarcodeInput,
	BARCODE_FIELD_LENGTH,
} from "../../utils/barcodeUtils";
import { shouldShowItemTaxField } from "../PharmacyManageTax/taxConfigUtils";
import "../PharmacyMedicineAdd/item-form.scss";

// OTC product forms (sorted alphabetically)
export const otcProductForms = [
	{ id: 1, name: "Balm / Stick", value: "balm" },
	{ id: 2, name: "Biscuits / Snacks", value: "snack" },
	{ id: 3, name: "Capsule", value: "capsule" },
	{ id: 4, name: "Chewable Tablet", value: "chewable_tablet" },
	{ id: 5, name: "Cream", value: "cream" },
	{ id: 6, name: "Drops", value: "drops" },
	{ id: 7, name: "Drinks / Beverages", value: "drink" },
	{ id: 8, name: "Effervescent Tablet", value: "effervescent_tablet" },
	{ id: 9, name: "Film / Oral Film", value: "oral_film" },
	{ id: 10, name: "Gel", value: "gel" },
	{ id: 11, name: "Gummy / Chewable Bar", value: "gummy" },
	{ id: 12, name: "Lotion", value: "lotion" },
	{ id: 13, name: "Lozenge / Troche", value: "lozenge" },
	{ id: 14, name: "Medicated Soap / Bar", value: "medicated_soap" },
	{ id: 15, name: "Nebulizer Solution", value: "nebulizer_solution" },
	{ id: 16, name: "Patch", value: "patch" },
	{ id: 17, name: "Powder", value: "powder" },
	{ id: 18, name: "Powder for Infusion / Injection (reconstitution)", value: "powder_for_infusion" },
	{ id: 19, name: "Powder for Suspension", value: "powder_for_suspension" },
	{ id: 20, name: "Syrup / Oral Solution", value: "syrup" },
	{ id: 21, name: "Soft Gelatin Capsule", value: "soft_gelatin_capsule" },
	{ id: 22, name: "Spray / Inhaler", value: "spray" },
	{ id: 23, name: "Suppository", value: "suppository" },
	{ id: 24, name: "Tablet", value: "tablet" },
	{ id: 25, name: "Other", value: "other" }
];

// OTC package types (sorted alphabetically)
export const otcPackageTypes = [
	{ id: 1, name: "Ampoule / Vial", value: "ampoule" },
	{ id: 2, name: "Blister Pack", value: "blister_pack" },
	{ id: 3, name: "Bottle", value: "bottle" },
	{ id: 4, name: "Box / Carton", value: "box" },
	{ id: 5, name: "Dropper + Bottle Combo", value: "dropper_combo" },
	{ id: 6, name: "Dropper Bottle", value: "dropper_bottle" },
	{ id: 7, name: "Foil Pack / Foil Sachet", value: "foil_pack" },
	{ id: 8, name: "Jar", value: "jar" },
	{ id: 9, name: "Jar with Screw Cap", value: "jar_screw_cap" },
	{ id: 10, name: "Pump Bottle", value: "pump_bottle" },
	{ id: 11, name: "Package", value: "package" },
	{ id: 12, name: "Roll-on / Pen Dispenser", value: "roll_on" },
	{ id: 13, name: "Sachet / Pouch", value: "sachet" },
	{ id: 14, name: "Spray Bottle / Aerosol", value: "spray_bottle" },
	{ id: 15, name: "Stick Pack / Stick Tube", value: "stick_pack" },
	{ id: 16, name: "Tube", value: "tube" },
	{ id: 17, name: "Tube with Nozzle", value: "tube_nozzle" },
	{ id: 18, name: "Other", value: "other" }
];

const UpdateOTC = () => {
	const { mdcId } = useParams();
	const { t } = useTranslation();
	const history = useNavigate();
	const { setLoading } = useLoader();
	const { pharmacyPOS } = usePharmacy();
	const disableScrollProps = useDisableScrollOnFocus();

	const createItemBarcode = useCallback(
		() => generateInternalEan13(pharmacyPOS?.hospital_id),
		[pharmacyPOS?.hospital_id]
	);

	const resolveBarcode = useCallback((existing) => {
		const trimmed = String(existing || "").trim();
		if (trimmed) return trimmed;
		return createItemBarcode();
	}, [createItemBarcode]);

	const [formData, setFormData] = useState({
		brandName: "",
		manufacturer: "",
		package: "",
		productForm: "",
		quantity: "",
		regularPrice: "",
		sellingPrice: "",
		barcode: "",
		note: "",
		minAlertQuantity: "",
		currency: "",
		tax: "",
		primaryUse: [],
		interactions: [],
		packaging_detail: "",
		metaValue: "",
	});

	const showTaxField = shouldShowItemTaxField(pharmacyPOS, "otc");

	const [errors, setErrors] = useState({});
	const [showBarcodePrint, setShowBarcodePrint] = useState(false);

	useEffect(() => {
		const fetchItem = async () => {
			try {
				setLoading(true);
				const body = {
					itemId: mdcId,
					clinicId: pharmacyPOS?.hospital_id,
				};
				const res = await getItemDetails(body);
				if (res?.data?.data) {
					const item = res.data.data;
					setFormData({
						brandName: item.brandName || "",
						manufacturer: item.manufacturer || "",
						package: item.package || "",
						productForm: item.productForm || "",
						quantity: item.packageQuantity || "",
						regularPrice: item.regularPrice || "",
						barcode: resolveBarcode(item.barcode),
						note: item.note || "",
						minAlertQuantity: item.minQuantityAlert || "",
						currency: item.currency || "INR",
						tax: item.tax || "",
						primaryUse: getPrimaryUseList(item),
						interactions: getInteractionsList(item),
						packaging_detail: getPackagingDetail(item),
						metaValue: getItemMetaValue(item),
					});
				}
			} catch (error) {
				console.error("Error fetching item:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchItem();
	}, [mdcId, pharmacyPOS?.hospital_id, resolveBarcode, setLoading]);

	const handleRegenerateBarcode = () => {
		setFormData((prev) => ({ ...prev, barcode: createItemBarcode() }));
		setErrors((prev) => ({ ...prev, barcode: "" }));
	};

	const handleChange = (e) => {
		const { name, value } = e.target;
		if (name === "barcode") {
			setFormData((prev) => ({
				...prev,
				barcode: sanitizeBarcodeInput(value),
			}));
			if (errors.barcode) {
				setErrors((prev) => ({ ...prev, barcode: "" }));
			}
			return;
		}
		setFormData((prev) => ({ ...prev, [name]: value }));
		if (errors[name]) {
			setErrors((prev) => ({ ...prev, [name]: "" }));
		}
	};

	// Only product name is required for OTC update
	const validate = () => {
		const newErrors = {};
		if (!formData.brandName?.trim()) {
			newErrors.brandName = t("Product name is required");
		}
		return newErrors;
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		const validationErrors = validate();
		if (Object.keys(validationErrors).length > 0) {
			setErrors(validationErrors);
			toast.warning(t("Please fill in all required fields"));
			return;
		}
		try {
			setLoading(true);
			const body = {
				itemId: mdcId,
				clinicId: pharmacyPOS?.hospital_id,
				brandName: formData.brandName,
				manufacturer: formData.manufacturer,
				package: formData.package,
				productForm: formData.productForm,
				packageQuantity: formData.quantity,
				regularPrice: formData.regularPrice,
				barcode: formData.barcode,
				note: formData.note,
				minQuantityAlert: formData.minAlertQuantity,
				currency: formData.currency,
				isOTC: true,
				...(showTaxField ? { tax: formData.tax } : {}),
			};
			appendOptionalItemLists(body, {
				primary_use: formData.primaryUse,
				interactions: formData.interactions,
				packaging_detail: formData.packaging_detail,
				value: formData.metaValue,
			});
			const response = await updateItemInfo(body);
			toast.success(response.data.message);
			gotoList();
		} catch (error) {
			console.error("Error updating item:", error);
		} finally {
			setLoading(false);
		}
	};

	const gotoList = () => {
		history(`${process.env.PUBLIC_URL}/items`);
	};

	return (
		<Fragment>
			<Breadcrumbs mainTitle={t("Update OTC Item")} parent={t("Dashboard")} title={t("Update OTC Item")} />
			<Row className="mt-2 item-form-page">
				<Col sm={12}>
					<Card className="item-form-card">
						<CardBody className="item-form-body">
							<form onSubmit={handleSubmit} className="item-form">
								<div className="item-form-toolbar">
									<div className="item-form-type">
										<label className="form-label">{t("Item Type")}</label>
										<div className="item-type-switch">
											<label className="item-type-option">
												<input type="radio" name="itemType" value="otc" checked readOnly disabled />
												<span>OTC</span>
											</label>
										</div>
									</div>

									<div className="item-form-toolbar-actions">
										{formData.barcode && (
											<Button
												color="primary"
												outline
												size="sm"
												className="item-form-back-btn"
												onClick={() => setShowBarcodePrint(true)}
											>
												{t("Print")}
											</Button>
										)}
										<button
											type="button"
											className="btn btn-primary d-flex align-items-center item-form-back-btn"
											onClick={gotoList}
										>
											<ArrowLeft size={15} className="me-1" /> {t("Back")}
										</button>
									</div>
								</div>

								<div className="item-form-section">
									<div className="item-form-section-title">{t("Product Details")}</div>
									<div className="row g-2">
										<div className="col-lg-7 col-md-8">
											<label className="form-label">
												{t("Product Name")}
												<span className="required-mark">*</span>
											</label>
											<input
												type="text"
												name="brandName"
												value={formData.brandName}
												onChange={handleChange}
												className={`form-control ${errors.brandName ? "is-invalid" : ""}`}
												placeholder={t("Enter product name")}
											/>
											{errors.brandName && <div className="text-danger field-error">{errors.brandName}</div>}
										</div>
										<div className="col-lg-5 col-md-4">
											<label className="form-label">{t("Manufacturer")}</label>
											<input
												type="text"
												name="manufacturer"
												value={formData.manufacturer}
												onChange={handleChange}
												className="form-control"
												placeholder={t("Manufacturer")}
											/>
										</div>
									</div>
								</div>

								<hr className="item-form-divider" />

								<div className="item-form-section">
									<div className="item-form-section-title">{t("Pack & Pricing")}</div>
									<div className="row g-2">
										<div className="col-lg-2 col-md-4 col-6">
											<label className="form-label">{t("Packaging")}</label>
											<input
												type="text"
												name="package"
												value={formData.package}
												onChange={handleChange}
												className="form-control"
												placeholder={t("Box, Bottle")}
											/>
										</div>
										<div className="col-lg-2 col-md-4 col-6">
											<label className="form-label">{t("Form")}</label>
											<input
												type="text"
												name="productForm"
												value={formData.productForm}
												onChange={handleChange}
												className="form-control"
												placeholder={t("Cream, Gel")}
											/>
										</div>
										<div className="col-lg-2 col-md-4 col-6">
											<label className="form-label">{t("Units / Pack")}</label>
											<input
												type="number"
												name="quantity"
												min={1}
												value={formData.quantity}
												onChange={handleChange}
												className="form-control"
												{...disableScrollProps}
											/>
										</div>
										<div className="col-lg-2 col-md-4 col-6">
											<label className="form-label">{t("Pack MRP")}</label>
											<input
												type="number"
												name="regularPrice"
												value={formData.regularPrice}
												onChange={handleChange}
												className="form-control"
												{...disableScrollProps}
											/>
										</div>
										{showTaxField && (
											<div className="col-lg-1 col-md-4 col-6">
												<label className="form-label">{t("Tax %")}</label>
												<input
													type="number"
													name="tax"
													min={0}
													value={formData.tax}
													onChange={handleChange}
													className="form-control"
													{...disableScrollProps}
													placeholder="0"
												/>
											</div>
										)}
										<div className="col-lg-2 col-md-4 col-6">
											<label className="form-label">{t("Currency")}</label>
											<select
												name="currency"
												value={formData.currency}
												onChange={handleChange}
												className="form-select"
												disabled
											>
												<option value="INR">INR (₹)</option>
											</select>
										</div>
										<div className="col-lg-2 col-md-4 col-6">
											<label className="form-label">{t("Min Stock Alert")}</label>
											<input
												type="number"
												name="minAlertQuantity"
												min={0}
												value={formData.minAlertQuantity}
												onChange={handleChange}
												className="form-control"
												{...disableScrollProps}
											/>
										</div>
									</div>
								</div>

								<div className="item-form-section mb-0">
									<div className="row g-2">
										<div className="col-lg-5 col-md-6">
											<label className="form-label">{t("Bar Code")}</label>
											<div className="input-group">
												<input
													type="text"
													name="barcode"
													value={formData.barcode}
													onChange={handleChange}
													className="form-control"
													placeholder={t("13-digit barcode")}
													maxLength={BARCODE_FIELD_LENGTH}
													inputMode="numeric"
													autoComplete="off"
													{...disableScrollProps}
												/>
												<button
													type="button"
													className="btn btn-outline-secondary z-0"
													onClick={handleRegenerateBarcode}
													title={t("Generate barcode")}
												>
													<i className="fa fa-refresh" aria-hidden="true" />
												</button>
											</div>
										</div>
										<div className="col-lg-7 col-md-6">
											<label className="form-label">{t("Notes")}</label>
											<input
												type="text"
												name="note"
												value={formData.note}
												onChange={handleChange}
												className="form-control"
												placeholder={t("Optional notes")}
											/>
										</div>
									</div>
								</div>

								<div className="item-form-actions">
									<button className="btn btn-primary" type="submit">
										{t("Update Item")}
									</button>
									<Button color="secondary" onClick={gotoList}>
										{t("Cancel")}
									</Button>
								</div>
							</form>
						</CardBody>
					</Card>
				</Col>
			</Row>

			<BarcodePrintModal
				show={showBarcodePrint}
				onHide={() => setShowBarcodePrint(false)}
				product={{
					barcode: formData.barcode,
					brandName: formData.brandName,
					saltName: formData.brandName,
					regularPrice: formData.regularPrice,
					currency: formData.currency,
				}}
			/>
		</Fragment>
	);
};

export default UpdateOTC;
